import { useEffect, useState } from "react";
import { UserRound, Calendar, Clock, CheckCircle2, XCircle, Loader2, Video } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import { consultationApi } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";
import { useNavigate } from "react-router-dom";

function statusStyle(s = "") {
  const v = s.toLowerCase();
  if (v.includes("complet") || v.includes("accept") || v.includes("confirm")) return { c: "#16A34A", Icon: CheckCircle2 };
  if (v.includes("cancel") || v.includes("reject") || v.includes("declin")) return { c: "#DC2626", Icon: XCircle };
  return { c: "#F59E0B", Icon: Clock };
}

export default function Consultations() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?._id) return;
    consultationApi
      .listPatient(user._id)
      .then((res) => {
        const d = res.data;
        const list = Array.isArray(d) ? d : d?.consultations || d?.data || d?.items || [];
        setItems(list);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [user?._id]);

  return (
    <div className="max-w-3xl" data-testid="consultations-page">
      <PageHeader title="My Consultations" subtitle="Your doctor appointments" icon={UserRound} color="#2563EB" />
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <Card className="rounded-3xl p-8 text-center border-border">
          <UserRound className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="mt-3 font-semibold">No consultations yet</p>
          <p className="text-sm text-muted-foreground mt-1">Book a doctor to get started.</p>
          <Button className="mt-4 rounded-2xl" onClick={() => navigate("/doctors")} data-testid="consult-book">Book a Doctor</Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((c, i) => {
            const st = statusStyle(c.status);
            const StIcon = st.Icon;
            return (
              <Card key={c.id || c._id || i} className="rounded-2xl p-4 border-border" data-testid={`consult-item-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{c.doctor_name || c.doctorName || c.doctor?.name || "Doctor"}</p>
                    <p className="text-sm text-muted-foreground truncate">{c.speciality || c.specialty || c.hospital_name || c.hospitalName || "Consultation"}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                      {(c.date || c.appointment_date || c.slot_date) && (
                        <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{c.date || c.appointment_date || c.slot_date}</span>
                      )}
                      {(c.time || c.slot_time) && (
                        <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{c.time || c.slot_time}</span>
                      )}
                    </div>
                  </div>
                  <span className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full shrink-0" style={{ backgroundColor: st.c + "1A", color: st.c }}>
                    <StIcon className="h-3.5 w-3.5" /> {c.status || "Pending"}
                  </span>
                </div>
                <Button
                  size="sm"
                  className="mt-3 w-full rounded-2xl text-white"
                  style={{ backgroundColor: "#0EA5A6" }}
                  onClick={() => navigate(`/consult/${c.id || c._id}/call`)}
                  data-testid={`consult-join-${i}`}
                >
                  <Video className="h-4 w-4 mr-1.5" /> Join Video Call
                </Button>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
